import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { getOrganizationApps } from "../api/query";
import type {
    GetOrganizationAppsQuery,
    GetOrganizationAppsQueryVariables,
} from "../api/query/getOrganizationApps.gql-generated";
import { useMutation, useQuery } from "@apollo/client";
import { AppListItemModel } from "../models";
import { createVersionForApp } from "../api/mutations/createVersionForApp";
import { createAppForOrg } from "../api/mutations/createAppForOrg";
import type {
    CreateAppVersionMutation,
    CreateAppVersionMutationVariables,
} from "../api/mutations/createVersionForApp.gql-generated";
import type { CreateAppMutation, CreateAppMutationVariables } from "../api/mutations/createAppForOrg.gql-generated";
import type {
    EnableCameraKitFeatureMutation,
    EnableCameraKitFeatureMutationVariables,
} from "../api/mutations/enableCameraKitForVersion.gql-generated";
import { enableCameraKitForVersion } from "../api/mutations/enableCameraKitForVersion";
import { type RouteParams, AbsoluteRoute } from "../../../core/routes";

type UseAppsProps = {
    setNewAppCb?: (appId: string) => void;
    setNewVersionCb?: (version: number) => void;
};

export function useApps({ setNewAppCb = () => {}, setNewVersionCb = () => {} }: UseAppsProps) {
    const { orgId } = useParams<RouteParams[AbsoluteRoute.ORG_APPS]>();

    const { loading, data, error } = useQuery<GetOrganizationAppsQuery, GetOrganizationAppsQueryVariables>(
        getOrganizationApps,
        {
            variables: {
                organizationId: orgId,
            },
        },
    );

    const [createApp, { loading: isCreatingApp }] = useMutation<CreateAppMutation, CreateAppMutationVariables>(
        createAppForOrg,
        {
            refetchQueries: [getOrganizationApps],
            onQueryUpdated: async (observableQuery) => {
                await observableQuery.refetch();
            },
            onCompleted: ({ createApplication }) => {
                const appId = createApplication?.appInfo?.appId;
                if (appId) {
                    setNewAppCb(appId);
                }
            },
        },
    );

    const [createVersion, { loading: isCreatingVersion }] = useMutation<
        CreateAppVersionMutation,
        CreateAppVersionMutationVariables
    >(createVersionForApp, {
        refetchQueries: [getOrganizationApps],
        onQueryUpdated: async (observableQuery) => {
            await observableQuery.refetch();
        },
        onCompleted: ({ createAppVersion }) => {
            const version = createAppVersion?.appVersion?.version;
            if (version) {
                setNewVersionCb(version);
            }
        },
    });

    const [enableCamKit, { loading: isEnabling }] = useMutation<
        EnableCameraKitFeatureMutation,
        EnableCameraKitFeatureMutationVariables
    >(enableCameraKitForVersion, {
        refetchQueries: [getOrganizationApps],
        onQueryUpdated: async (observableQuery) => {
            await observableQuery.refetch();
        },
    });

    const apps = useMemo(() => data?.getAppList?.map(AppListItemModel.createAppListItem) || [], [data]);

    return {
        apps,
        loading,
        error,
        createApp,
        isCreatingApp,
        createVersion,
        isCreatingVersion,
        enableCamKit,
        isEnabling,
    };
}
